import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { isVideoMediaUrl } from "@/lib/mediaUrl";
import { cn } from "@/lib/utils";

type CleanerMediaLightboxProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  urls: string[];
  initialIndex?: number;
};

/**
 * Full-screen media viewer for cleaner flows (host reference photos/videos).
 * Keeps cleaners on the current screen instead of opening raw storage URLs in a new tab.
 */
export function CleanerMediaLightbox({
  open,
  onOpenChange,
  urls,
  initialIndex = 0,
}: CleanerMediaLightboxProps) {
  const safeUrls = useMemo(
    () => urls.filter((u): u is string => typeof u === "string" && u.trim().length > 0),
    [urls],
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open || safeUrls.length === 0) return;
    const clamped = Math.min(Math.max(0, initialIndex), safeUrls.length - 1);
    setIndex(clamped);
  }, [open, initialIndex, safeUrls.length]);

  const goNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % safeUrls.length);
  }, [safeUrls.length]);

  const goPrev = useCallback(() => {
    setIndex((prev) => (prev - 1 + safeUrls.length) % safeUrls.length);
  }, [safeUrls.length]);

  if (safeUrls.length === 0) return null;

  const url = safeUrls[index];
  const video = isVideoMediaUrl(url);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-w-[100vw] w-screen h-[100dvh] max-h-[100dvh] p-0 bg-black border-none rounded-none",
          "sm:rounded-lg sm:max-w-4xl sm:h-auto sm:max-h-[90vh]",
          "[&>button.absolute]:hidden",
        )}
        aria-describedby={undefined}
      >
        <div className="relative flex min-h-[60vh] w-full items-center justify-center bg-black touch-pan-y sm:min-h-[50vh]">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 sm:h-10 sm:w-10"
            aria-label="Close"
          >
            <X className="h-6 w-6 text-white sm:h-5 sm:w-5" />
          </button>

          {safeUrls.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-2 z-10 hidden h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 sm:flex"
                aria-label="Previous"
              >
                <ChevronLeft className="h-6 w-6 text-white" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-2 z-10 hidden h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 sm:flex"
                aria-label="Next"
              >
                <ChevronRight className="h-6 w-6 text-white" />
              </button>
            </>
          )}

          {video ? (
            <video src={url} controls className="max-h-[75vh] max-w-full object-contain px-2 sm:max-h-[80vh]" />
          ) : (
            <img
              src={url}
              alt=""
              className="max-h-[75vh] max-w-full object-contain px-2 sm:max-h-[80vh]"
              loading="lazy"
            />
          )}

          {safeUrls.length > 1 && (
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/30 p-2">
              {safeUrls.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={cn(
                    "rounded-full transition-all",
                    "h-3 w-3 sm:h-2 sm:w-2",
                    i === index ? "scale-110 bg-white" : "bg-white/40",
                  )}
                  aria-label={`Go to item ${i + 1}`}
                />
              ))}
            </div>
          )}

          <div className="absolute left-4 top-4 rounded-full bg-black/30 px-3 py-1 text-sm font-medium text-white/80">
            {index + 1} / {safeUrls.length}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
